import { useState } from "react";
import { Alert } from "@/ui/alert";
import { Input } from "@/ui/input";
import * as api from "../api.js";
import { ICONES, Icone, mmss } from "../visual.jsx";
import { useAcoes } from "../estado.jsx";
import { useGravacaoReuniao } from "../funcionalidades/reunioes/useGravacaoReuniao.ts";
import { MODOS, comModo, lerConfig, modoDe } from "../funcionalidades/reunioes/config.ts";
import AjustesDaGravacao from "../sobreposicoes/AjustesDaGravacao.jsx";
import { Botao, Seletor } from "./comuns.jsx";

/**
 * O gravador de reunião — um só, e é este.
 *
 * Antes havia dois: um `MediaRecorder` de quinze linhas no estado da mesa (que o
 * botão do cabeçalho ligava sem avisar) e o estúdio completo da vista Reuniões.
 * O bom era o da vista, e estava na tela errada: gravar reunião é dentro do
 * atendimento, com a ficha e o checklist do lado. Então o motor veio para cá e o
 * pobre morreu — pausa de verdade, medidor por trilha, marcar momento com rótulo
 * e prévia ao vivo passaram a existir onde alguém grava.
 *
 * Padrão é só o microfone (o alinhamento desta casa é o advogado ao telefone).
 * "Chamada online" — as duas trilhas, que é o que dá a diarização — é uma escolha
 * nos ajustes, e fica guardada na máquina de quem grava.
 */

/** Um medidor de trilha: barras e o pico em dB. */
function Medidor({ rotulo, barras, db, ligada }) {
  return (
    <div className="min-w-[96px] flex-1">
      <div className="mb-[3px] flex items-baseline justify-between">
        <span className={`text-selo font-bold tracking-[.08em] ${ligada ? "text-verde-claro" : "text-sobre-escuro/60"}`}>{rotulo}</span>
        <span className="font-mono text-selo text-sobre-escuro/60">{db == null ? (ligada ? "sem som" : "desligado") : `${db} dB`}</span>
      </div>
      <div className="flex h-4 items-end gap-[1.5px]">
        {barras.map((altura, i) => (
          <span
            key={i}
            /* A altura de cada barra é o nível do momento: é conta em execução, e
               por isso continua no `style`. */
            className={`min-w-px flex-1 rounded-[2px] ${altura > 0.02 ? "bg-verde-vivo" : "bg-sobre-escuro/20"}`}
            style={{ height: `${Math.max(8, altura * 100)}%` }}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * A pele do painel do atendimento — a única que existe: a vista Reuniões deixou
 * de gravar, e a pele larga que sobrava para ela saiu junto.
 *
 * Parado, é UMA linha — o botão e o modo, sem a ajuda escrita e sem o parágrafo
 * do compartilhamento de aba (que só interessa depois de escolher "chamada
 * online", e aí o próprio navegador pergunta). Gravando, o cartão escuro GRUDA
 * no alto da coluna: a faixa "GRAVANDO 04:12" precisa estar à vista de qualquer
 * altura da rolagem, que é exatamente o que a aba antiga não fazia — o
 * microfone ficava ligado atrás de uma aba fechada.
 */
export default function GravadorDeReuniao({ aberto = true }) {
  const { guardarReuniao, avisar } = useAcoes();
  const [config, setConfig] = useState(lerConfig);
  const [ajustesAbertos, setAjustesAbertos] = useState(false);
  /* Quem tocou na linha recolhida quer os controles: e quer que eles FIQUEM,
     porque o passo seguinte é escolher o modo antes de gravar. */
  const [expandido, setExpandido] = useState(false);
  const [rotuloDoMomento, setRotuloDoMomento] = useState("");

  const gravacao = useGravacaoReuniao({
    config,
    aoTranscreverPedaco: config.previaAoVivo ? api.transcreverPedaco : undefined,
    aoTerminar: (g) => guardarReuniao(g, { config }),
  });

  const { estado, segundos, niveis, ganho, pausado, marcadores, previa, aviso, erro, suportado, seguro } = gravacao;
  const gravando = estado === "gravando";
  const modo = MODOS.find((m) => m.id === modoDe(config));

  if (!suportado)
    return (
      /* `role="status"` no lugar do `alert` que o componente traz: isto é a
         condição do navegador, não a resposta a um clique — e quem abre a aba não
         precisa ser interrompido para saber que não vai gravar por aqui. */
      <Alert
        role="status"
        className="mx-4 mb-3 rounded-cartao border-ambar/35 bg-ambar/9 p-3 text-apoio leading-[1.5] text-ambar-escuro"
      >
        {seguro
          ? "Este navegador não grava áudio. Chrome, Edge ou Firefox atualizados gravam."
          : "O navegador só libera o microfone em HTTPS ou em localhost. Abra a mesa por um desses — o navegador não é o problema."}
      </Alert>
    );

  /*
   * Parado e fora da hora: UMA linha, e ela abre.
   *
   * O gravador inteiro (botão + modo + frase) são 130px, e num caso parado em
   * documentos ele era o segundo bloco da coluna — acima do que havia para
   * fazer. Fechado ele custa 34px e continua dizendo que existe; um toque traz
   * os controles de volta. A instância é a mesma, então nada se perde.
   */
  if (estado === "ocioso" && !aberto && !expandido)
    return (
      <div className="px-4 pt-1 pb-2.5">
        <Botao
          aoTocar={() => setExpandido(true)}
          titulo="Gravar a reunião de alinhamento deste caso"
          classe="w-full justify-start text-tinta-suave"
          rotulo={
            <>
              <Icone de={ICONES.gravando} tamanho={16} cor="var(--color-terra)" />
              Gravar reunião
            </>
          }
        />
      </div>
    );

  if (estado === "ocioso")
    return (
      /*
       * DUAS fileiras na coluna, e não uma.
       *
       * Era `[botão flex-1] [seletor de 132px] [engrenagem]` numa fileira só. A
       * coluna do atendimento tem 300px, e 32 são de respiro: sobram 268 para
       * três controles com dois vãos. O resultado na tela do escritório era o
       * seletor dizendo "Só meu micro…" e a engrenagem cortada pela borda — o
       * controle que decide se a reunião sai COM quem falou o quê, ilegível e
       * meio fora da vista.
       *
       * A ação primária ganha a largura inteira (é uma por passo, e é o que a
       * pessoa vem fazer); o modo desce para a linha de baixo, também inteira, e
       * a engrenagem fica ao lado dele — que é onde ela pertence, porque os dois
       * são sobre COMO gravar, não sobre gravar.
       */
      <div className="flex flex-col gap-1.5 px-4 pt-1 pb-3">
        <Botao
          aoTocar={gravacao.gravar}
          variante="destaque"
          classe="w-full"
          rotulo={
            <>
              <Icone de={ICONES.gravando} tamanho={16} cor="var(--color-terra)" />
              Gravar reunião
            </>
          }
        />
        {/*
          O modo à mão, e não atrás de AJUSTES: é ele que decide se a reunião sai
          COM quem falou o quê. Escondido, a decisão era tomada por omissão —
          grava-se o Meet com o microfone só, e a transcrição vem corrida, sem
          atribuição, sem chance de refazer a reunião.
        */}
        <div className="flex items-center gap-1.5">
          <span className="min-w-0 flex-1">
            <Seletor
              rotulo="Como gravar"
              largura="100%"
              valor={modoDe(config)}
              aoEscolher={(id) => setConfig(comModo(config, id))}
              opcoes={MODOS.map((m) => ({ id: m.id, rotulo: m.rotulo }))}
            />
          </span>
          <Botao
            icone={ICONES.settings}
            aoTocar={() => setAjustesAbertos(true)}
            titulo="Ajustes da gravação — microfone, qualidade e leitura de nome"
            classe="alvo"
          />
        </div>
        {/* A frase do modo escolhido: é ela que diz o que vai acontecer com a
            trilha do cliente, e ela cabe aqui embaixo sem apertar nada. */}
        {modo?.ajuda ? <div className="text-apoio leading-[1.4] text-tinta-fraca">{modo.ajuda}</div> : null}
        {erro ? <div className="text-apoio leading-[1.45] text-destructive">{erro}</div> : null}
        {ajustesAbertos ? (
          <AjustesDaGravacao config={config} aoMudar={setConfig} aoFechar={() => setAjustesAbertos(false)} />
        ) : null}
      </div>
    );

  return (
    <div
      /* Gruda logo abaixo do cabeçalho preso, pela altura MEDIDA dele
         (`CabecalhoDoPainel` escreve a variável): os 74px fixos deixavam um vão
         de 18px sobre um cabeçalho de 56. */
      className="sticky top-[var(--altura-cabecalho-painel,56px)] z-[2] mx-4 mb-3 rounded-superficie bg-escuro p-[13px_14px] text-sobre-escuro shadow-[0_10px_28px_color-mix(in_oklab,_var(--color-foreground)_20%,_transparent)]"
    >
      <div className="mb-[11px] flex items-center gap-[9px]">
        <span
          className={`size-[9px] flex-none rounded-full ${
            gravando && !pausado ? "animate-[vPulse_1.2s_infinite] bg-terra" : "bg-sobre-escuro/40"
          }`}
        />
        <span className="text-apoio font-bold tracking-[.08em]">
          {estado === "preparando" && "ESCOLHA A ABA"}
          {gravando && (pausado ? "PAUSADA" : "GRAVANDO")}
          {estado === "enviando" && "TRANSCREVENDO"}
        </span>
        <span className="ml-auto font-mono text-corpo tabular-nums">{mmss(segundos)}</span>
      </div>

      <div className="mb-[11px] flex gap-3">
        <Medidor rotulo="MICROFONE" barras={niveis.mic} db={ganho.mic} ligada={config.gravarMic} />
        {config.gravarAba ? <Medidor rotulo="ÁUDIO DA ABA" barras={niveis.aba} db={ganho.aba} ligada /> : null}
      </div>

      {gravando ? (
        <>
          <Input
            /* `campo` continua aqui: é a classe que devolve o anel de foco da casa
               ao campo (o `outline:none` global tirou de todos). */
            className="campo mb-2 h-padrao w-full rounded-controle border-sobre-escuro/20 bg-sobre-escuro/8 px-3 text-apoio text-sobre-escuro placeholder:text-sobre-escuro/60"
            value={rotuloDoMomento}
            onChange={(e) => setRotuloDoMomento(e.target.value)}
            placeholder="Rótulo do próximo momento…"
            aria-label="Rótulo do próximo momento marcado"
          />
          <div className="flex flex-wrap gap-1.5">
            {/* `sobreEscuro`: o contorno do registro clareava para o `bg-muted`
                no ponteiro, e a letra creme sumia no fundo claro. */}
            <Botao
              variante="sobreEscuro"
              tamanho="mini"
              aoTocar={() => {
                const segundo = gravacao.marcar(rotuloDoMomento);
                setRotuloDoMomento("");
                avisar(`Momento marcado em ${mmss(segundo)}.`);
              }}
              rotulo={`Marcar momento${marcadores.length ? ` (${marcadores.length})` : ""}`}
            />
            <Botao
              variante="sobreEscuro"
              tamanho="mini"
              icone={pausado ? ICONES.tocar : ICONES.pausar}
              aoTocar={gravacao.alternarPausa}
              rotulo={pausado ? "Retomar" : "Pausar"}
            />
            <Botao variante="acao" tamanho="mini" classe="ml-auto" aoTocar={gravacao.parar} rotulo="Encerrar e transcrever" />
          </div>
        </>
      ) : null}

      {previa.length ? (
        <div className="mt-[11px] max-h-[150px] overflow-y-auto border-t border-t-sobre-escuro/12 pt-[9px] text-apoio leading-[1.55] text-sobre-escuro/86">
          {previa.join(" ")}
        </div>
      ) : null}

      {aviso ? <div className="mt-[9px] text-apoio leading-[1.45] text-ambar-claro">{aviso}</div> : null}
      {erro ? <div className="mt-[9px] text-apoio leading-[1.45] text-terra-clara">{erro}</div> : null}
    </div>
  );
}
